import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, AlertTriangle, Loader2, RefreshCw, Square } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { formatWhen } from "@/lib/xeroRunFormat";
import {
  CCH_PIPELINE_RUNS_QUERY_KEY,
  CCH_TRIGGER_LABEL,
  cancelCchPipelineRun,
  cchPipelineRunQueryKey,
  isCchRunInFlight,
  type CchPipelineRun,
  type CchTriggerStatus,
} from "@/services/cchApi";

/** Fabric job-instance state, coloured for the console. `running` gets a
 *  distinct colour because it is the thing the operator is watching for. */
const TRIGGER_STYLES: Record<CchTriggerStatus, string> = {
  queued: "border-amber-200 bg-amber-50 text-amber-700",
  running: "border-sky-200 bg-sky-50 text-sky-700",
  completed: "border-emerald-200 bg-emerald-50 text-emerald-700",
  failed: "border-red-200 bg-red-50 text-red-700",
  cancelled: "border-border bg-muted text-muted-foreground",
  unknown: "border-border bg-muted text-muted-foreground",
};

interface Props {
  run: CchPipelineRun;
  isFetching: boolean;
  onRefresh: () => void;
  onOpenRun?: (runId: string) => void;
}

export default function CchPipelineRunCard({
  run,
  isFetching,
  onRefresh,
  onOpenRun,
}: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const inFlight = isCchRunInFlight(run.status);

  const cancel = useMutation({
    mutationFn: () => cancelCchPipelineRun(run.run_id),
    onSuccess: (updated) => {
      queryClient.setQueryData(cchPipelineRunQueryKey(run.run_id), updated);
      queryClient.invalidateQueries({ queryKey: CCH_PIPELINE_RUNS_QUERY_KEY });
      toast({
        title: "Cancellation requested",
        description:
          "Fabric stops the run asynchronously; the status updates on the next poll.",
      });
      onOpenRun?.(updated.run_id);
    },
    onError: (e) => {
      toast({
        variant: "destructive",
        title: "Couldn’t cancel the run",
        description: (e as Error).message,
      });
    },
  });

  return (
    <section className="space-y-4 rounded-lg border border-border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className={`font-normal ${TRIGGER_STYLES[run.status]}`}
            >
              {run.status}
            </Badge>
            <span className="text-sm font-medium text-foreground">
              {CCH_TRIGGER_LABEL[run.trigger]} run
            </span>
            {inFlight && (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="font-mono">{run.run_id}</span>
            <span aria-hidden>·</span>
            <span>Triggered {formatWhen(run.triggered_at)}</span>
            {run.started_at && (
              <>
                <span aria-hidden>·</span>
                <span>Started {formatWhen(run.started_at)}</span>
              </>
            )}
            {run.finished_at && (
              <>
                <span aria-hidden>·</span>
                <span>Finished {formatWhen(run.finished_at)}</span>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={isFetching}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {inFlight && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => cancel.mutate()}
              disabled={cancel.isPending || !run.fabric_job_instance_id}
              className="gap-2 text-destructive hover:text-destructive"
            >
              {cancel.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Square className="h-4 w-4" />
              )}
              Cancel
            </Button>
          )}
        </div>
      </div>

      {run.poll_error && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Fabric status could not be read: {run.poll_error} Showing the last
            known state.
          </span>
        </div>
      )}

      {run.failure_reason && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{run.failure_reason}</span>
        </div>
      )}

      <dl className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
        <Field label="Trigger" value={CCH_TRIGGER_LABEL[run.trigger]} />
        <Field label="Fabric job" value={run.fabric_job_instance_id ?? "—"} mono />
        <Field label="Triggered by" value={run.triggered_by ?? "—"} />
        <Field label="Last polled" value={formatWhen(run.last_polled_at)} />
      </dl>
    </section>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd
        className={`truncate text-sm text-foreground ${mono ? "font-mono" : ""}`}
        title={value}
      >
        {value}
      </dd>
    </div>
  );
}
